import { useEffect, useRef, useState } from 'react'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { isQuietHours } from 'app/lib/smsQuietHours'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

type RowWithLocation = { locationId?: string | null }

/**
 * Fetches the IANA timezone for every distinct location in the current
 * selection, merging results into a map that survives across selections (a
 * location's timezone is a per-location fact).
 *
 * Pass `enabled` only while the SMS modal is open. `allKnown` is false until
 * every distinct location in the current selection has been fetched, so a
 * changed selection never reads off a partial or previous map. A generation
 * counter drops results from a fetch the selection has since outdated.
 */
function useLocationTimezones(
  rows: RowWithLocation[],
  enabled: boolean,
): { timezones: Map<string, string | undefined>; allKnown: boolean } {
  const [timezones, setTimezones] = useState(() => new Map<string, string | undefined>())
  const generationRef = useRef(0)

  useEffect(() => {
    if (!enabled) return
    const generation = ++generationRef.current
    const ids = [...new Set(rows.map((row) => row.locationId).filter((id): id is string => !!id))]
    bg(async () => {
      const entries = await Promise.all(
        ids.map(async (id): Promise<[string, string | undefined]> => {
          try {
            const location = await gqlFetchOne<{ timezone?: string | null }>(`location`, id)
            return [id, location?.timezone ?? undefined]
          } catch (err) {
            logError(`Bulk SMS quiet-hours check could not load location ${id}`, err)
            return [id, undefined]
          }
        }),
      )
      if (generation !== generationRef.current) return
      setTimezones((prev) => new Map([...prev, ...entries]))
    })
  }, [enabled, rows])

  if (!enabled) return { timezones, allKnown: false }
  const allKnown = rows.every((row) => !row.locationId || timezones.has(row.locationId))
  return { timezones, allKnown }
}

/**
 * The quiet-hours count plus the one shared practice timezone for the current
 * selection, for the bulk SMS modal's warning banner and schedule control.
 *
 * The send loop re-checks quiet hours per row at send time and stays
 * authoritative; `quietSkipCount` only drives the warning. `timezone` comes
 * back undefined when the selected rows span more than one zone, since there
 * is no single clock to show the picker; `mixedTimezones` distinguishes that
 * case from "not loaded yet" so the modal can explain the missing clock.
 */
export function useSmsSchedulingSelection(
  rows: RowWithLocation[],
  enabled: boolean,
): { quietSkipCount: number; timezone?: string; mixedTimezones: boolean } {
  const { timezones, allKnown } = useLocationTimezones(rows, enabled)
  if (!enabled || !allKnown) return { quietSkipCount: 0, mixedTimezones: false }
  const quietSkipCount = rows.filter((row) => row.locationId && isQuietHours(timezones.get(row.locationId))).length
  const distinctTimezones = new Set(
    rows.map((row) => (row.locationId ? timezones.get(row.locationId) : undefined)).filter((tz): tz is string => !!tz),
  )
  const timezone = distinctTimezones.size === 1 ? [...distinctTimezones][0] : undefined
  return { quietSkipCount, timezone, mixedTimezones: distinctTimezones.size > 1 }
}
